import { useTranslation } from 'react-i18next';
import { Globe, BookOpen, Trophy } from 'lucide-react';
import { ACTIVE_VARIANTS } from '@draughts/shared';
import {
  buildGameRulesPath,
  buildTournamentRulesPath,
  isRulesLang,
  type RulesLang,
} from '../../seo/routes';
import '../shared/sweep-ring.css';
import './Rules.css';

interface RulesIndexPageProps {
  /** All'elenco delle varianti: la meta' «regole di gioco» della sezione. */
  onOpenGameRules: () => void;
  /** Alla sotto-pagina del regolamento dei tornei. */
  onOpenTournamentRules: () => void;
}

/**
 * L'indice della sezione regole, e da oggi SOLO un bivio: le regole di questo
 * sito sono due — quelle del gioco e quelle dei tornei — e sono pari.
 *
 * Prima questa pagina ERA le regole di gioco (la griglia delle ventiquattro
 * varianti) con i tornei appesi sotto, e quindi diceva che il regolamento dei
 * tornei stava DENTRO le regole di gioco. La griglia ha traslocato su
 * RulesGamePage; qui restano due porte, e nessuna delle due e' figlia
 * dell'altra.
 */
export default function RulesIndexPage({
  onOpenGameRules,
  onOpenTournamentRules,
}: RulesIndexPageProps) {
  const { t, i18n } = useTranslation();
  const lang: RulesLang = isRulesLang(i18n.language) ? i18n.language : 'en';

  return (
    <div className="rl-page">
      <div className="rl-inner">
        {/* hero */}
        <div className="rl-card rl-hero rl-hero-3">
          <div className="rl-hero-deco"><div className="rl-hero-king" /></div>
          <div className="rl-hero-center">
            <span className="rl-eyebrow">{t('rules.eyebrow')}</span>
            <h1 className="rl-hero-title">{t('rules.indexTitle')}</h1>
            <p className="rl-hero-sub">{t('rules.indexIntro')}</p>
          </div>
          <div className="rl-hero-chip">
            <span className="rl-chip-ico"><Globe /></span>
            <span className="rl-chip-txt">
              <span className="rl-chip-v">{ACTIVE_VARIANTS.length}</span>
              <span className="rl-chip-l">{t('rules.variantsLabel')}</span>
            </span>
          </div>
        </div>

        {/*
          Le due porte, figlie dirette di `.rl-inner` e senza intestazione di
          sezione sopra: sono l'intero contenuto della pagina, e un titolo che
          le raggruppa ripeterebbe l'H1.

          Stesso impianto delle due card d'ingresso sulla pagina di una
          variante, impilate a tutta larghezza a ogni misura — l'anello di luce
          e' un elemento vero e non uno pseudo-elemento, perche' maschera e
          gradiente vogliono due layer (vedi shared/sweep-ring.css).

          Link veri e non bottoni: l'hub e' l'unico ponte verso le due
          sottosezioni, e un crawler deve poterlo attraversare. E' la stessa
          lezione scritta sulla griglia delle varianti: finche' era fatta di
          <button>, le pagine sotto erano isole raggiungibili solo dal sitemap.
        */}
        <a
          className="rl-card rl-op-entry"
          href={buildGameRulesPath(lang)}
          onClick={(e) => { e.preventDefault(); onOpenGameRules(); }}
        >
          <span className="sweep-ring" aria-hidden="true" />
          <span className="rl-op-entry-ico"><BookOpen /></span>
          <span className="rl-op-entry-txt">
            <span className="rl-op-entry-title">{t('rules.gameRulesHead')}</span>
            <span className="rl-op-entry-desc">{t('rules.gameCtaDesc')}</span>
          </span>
          <span className="rl-op-entry-arrow" aria-hidden="true">→</span>
        </a>

        <a
          className="rl-card rl-op-entry"
          href={buildTournamentRulesPath(lang)}
          onClick={(e) => { e.preventDefault(); onOpenTournamentRules(); }}
        >
          <span className="sweep-ring" aria-hidden="true" />
          <span className="rl-op-entry-ico"><Trophy /></span>
          <span className="rl-op-entry-txt">
            <span className="rl-op-entry-title">{t('rules.tournaments.title')}</span>
            <span className="rl-op-entry-desc">{t('rules.tournaments.ctaDesc')}</span>
          </span>
          <span className="rl-op-entry-arrow" aria-hidden="true">→</span>
        </a>
      </div>
    </div>
  );
}
